import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { theme, message } from 'antd';
import { observer } from 'mobx-react';
import { useState } from 'react';

import { useStores } from '@/hooks/use-stores';
import { themeNames, useTheme } from '@/theme';
import UserIcon from '@/assets/icon-user.svg?react';
import ArrowIcon from '@/comp/common/icons/ArrowIcon';
import { LanguageEnum, UnitMeasurementSystem } from '@/constants/enums';
import MeasurementUnitIcon from '@/comp/common/icons/MeasurementUnitIcon';
import LanguageIcon from '@/comp/common/icons/LanguageIcon';
import ThemeIcon from '@/comp/common/icons/ThemeIcon';
import LogoutIcon from '@/comp/common/icons/LogoutIcon';
import { SpinCentered } from '@/comp';

import { signOutHandler } from '../../StationPage.utils';
import { GlobalPopoverMenuUserStyle, PopoverMenuUserStyled } from './PopoverMenuUser.styles';

const { useToken } = theme;

const PopoverMenuUser = observer(() => {
  const { userStore, usersStore } = useStores();
  const [isUpdating, setIsUpdating] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { changeTheme, name: themeName } = useTheme();
  const { token } = useToken();

  const onChangeMeasurementUnit = async ({ key }: { key: string }) => {
    setIsUpdating(true);
    await usersStore.update(
      userStore.user.id,
      { measurementSystem: key },
      'Measurement unit updated successfully',
      'Measurement unit update failed',
    );
    await userStore.getMe();
    setIsUpdating(false);
  };

  const onChangeTheme = async ({ key }: { key: string }) => {
    setIsUpdating(true);
    await changeTheme(key);
    localStorage.setItem('theme', key);
    message.success(t('Theme updated successfully'));
    setIsUpdating(false);
  };

  const onChangeLanguage = async ({ key }: { key: string }) => {
    setIsUpdating(true);
    await usersStore.update(
      userStore.user.id,
      { language: key },
      t('languageUpdateSuccess'),
      t('languageUpdateFailed'),
    );
    await i18n.changeLanguage(key);
    localStorage.setItem('language', key);
    setIsUpdating(false);
  };
  return (
    <>
      <GlobalPopoverMenuUserStyle />
      <PopoverMenuUserStyled
        $isUpdating={isUpdating || usersStore.isUpdating}
        expandIcon={null}
        selectedKeys={[userStore.user.measurementSystem, themeName, localStorage.getItem('language')]}
        items={[
          {
            key: 'user',
            label: isUpdating ? (
              <SpinCentered fontSize={token.fontSizeHeading3} className="user-icon" size="small" text={null} />
            ) : (
              <UserIcon className="user-icon" />
            ),
            popupClassName: 'popover-menu-user',
            children: [
              {
                key: 'user-name',
                type: 'group',
                label: (
                  <div>
                    <div className="user-name">
                      {userStore.user.firstName} {userStore.user.lastName}
                    </div>
                    <div className="user-email">{userStore.user.email}</div>
                  </div>
                ),
              },
              {
                type: 'divider',
              },
              {
                key: 'measurementSystem',
                label: (
                  <div className="menu-item">
                    <div className="menu-item-content">
                      <MeasurementUnitIcon />
                      {t('measurementSystem')}
                    </div>
                    <ArrowIcon className="expand-icon" />
                  </div>
                ),
                children: [UnitMeasurementSystem.Metric, UnitMeasurementSystem.Imperial].map(measurementSystem => ({
                  key: measurementSystem,
                  label: t(measurementSystem),
                  onClick: onChangeMeasurementUnit,
                })),
              },

              {
                key: 'theme',
                label: (
                  <div className="menu-item">
                    <div className="menu-item-content">
                      <ThemeIcon />
                      {t('theme')}
                    </div>
                    <ArrowIcon className="expand-icon" />
                  </div>
                ),
                children: themeNames.map(themeName => ({
                  key: themeName,
                  label: t(themeName),
                  onClick: onChangeTheme,
                })),
              },
              {
                key: 'language',
                label: (
                  <div className="menu-item">
                    <div className="menu-item-content">
                      <LanguageIcon />
                      {t('language')}
                    </div>
                    <ArrowIcon className="expand-icon" />
                  </div>
                ),
                children: [LanguageEnum.EN, LanguageEnum.FR, LanguageEnum.ES, LanguageEnum.PT, LanguageEnum.DE].map(
                  language => ({
                    key: language,
                    label: t(language),
                    onClick: onChangeLanguage,
                  }),
                ),
              },
              {
                type: 'divider',
              },
              {
                key: 'logout',
                label: (
                  <div className="menu-item-content">
                    <LogoutIcon width={16} height={16} color={token.colorIcon} />
                    {t('Log out')}
                  </div>
                ),
                onClick: signOutHandler(userStore, navigate),
              },
            ],
          },
        ]}
      />
    </>
  );
});

export default PopoverMenuUser;
